import React, { useContext, useEffect, useMemo, useState, useRef } from 'react'
import axios from '../../../../config/axios'
import { useOutletContext } from 'react-router-dom'
import { PeopleOutletType } from '..'
import TenantValidation from './TenantValidation'
import ModalContext from '../../../../contexts/ModalContext'
import { Pencil } from 'lucide-react'
import { StandardDataTable, StandardColumn, useColumnsState } from '../../../../components/StandardPage'

interface Tenant {
  id: number
  companyName: string
  segment: string
  cnpj: string
  adminUserId: number
  adminEmail: string
  approved: string
}

const tenantsScreenKey = 'tenants'

const buildTenantColumns = (onUpdate: () => void): StandardColumn<Tenant>[] => [
  { id: 'id', label: 'ID' },
  { id: 'companyName', label: 'Empresa' },
  { id: 'segment', label: 'Segmento' },
  { id: 'cnpj', label: 'CNPJ' },
  { id: 'adminEmail', label: 'Email Admin' },
  {
    id: 'approved',
    label: 'Status',
    sortable: false,
    cell: (tenant) => (
      <TenantValidation
        situation={tenant.approved}
        tenantId={tenant.id}
        adminUserId={tenant.adminUserId}
        onUpdate={onUpdate}
      />
    ),
  },
]

const TenantTable = () => {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loadQuantity, setLoadQuantity] = useState(50)
  const [hasMore, setHasMore] = useState(true)
  const [totalTenants, setTotalTenants] = useState<number | null>(null)
  const { search, refresh, setLoadingHeader } = useOutletContext<PeopleOutletType>()
  const { setModalActive, setModalSection, setSelectedTenant, setRefreshCallback } = useContext(ModalContext)

  const fetchTenants = async () => {
    setLoadingHeader?.(true)
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/tenant`, {
        params: {
          search,
          limit: loadQuantity,
          offset: 0,
        }
      })

      const data = Array.isArray(response.data) ? response.data : response.data.data || []
      const total = typeof response.data === 'object' && typeof response.data.total === 'number'
        ? response.data.total
        : undefined

      setTenants(data)
      setHasMore(typeof total === 'number' ? data.length < total : false)
      setTotalTenants(typeof total === 'number' ? total : null)
    } catch (error) {
      console.error('Erro ao buscar tenants:', error)
      setTenants([])
      setHasMore(false)
    } finally {
      setLoadingHeader?.(false)
    }
  }

  useEffect(() => {
    setRefreshCallback(() => fetchTenants)
  }, [])

  const isFirstRenderForTenants = useRef(true)
  useEffect(() => {
    if (isFirstRenderForTenants.current) {
      isFirstRenderForTenants.current = false
      return
    }
    if (refresh) fetchTenants()
  }, [refresh])

  useEffect(() => {
    fetchTenants()
  }, [search, loadQuantity])

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setModalSection('editTenant')
    setModalActive(true)
  }

  const columns = useMemo(() => buildTenantColumns(fetchTenants), [search, loadQuantity])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    tenantsScreenKey,
    columns,
  )

  return (
    <StandardDataTable
      columns={columns}
      data={tenants}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      totalCount={totalTenants}
      onLoadMore={() => setLoadQuantity(prev => prev + 50)}
      hasMore={hasMore}
      countLabel="tenants"
      exportFileName="tenants"
      rowActions={(tenant) => [
        { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEditTenant(tenant) },
      ]}
      emptyMessage="Nenhum tenant encontrado"
    />
  )
}

export default TenantTable
